/** @format */

import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Environment } from '@shared/data-access/interfaces';

@Directive({
  selector: '[sharedUtilsDirectiveCommonLazyLoad]',
  host: {
    '[attr.src]': 'srcAttr()',
    '[class.loaded]': 'chkLoaded()',
    '[class.redraw]': 'redraw()',
    '[style.display]': 'display()',
  },
})
export class LazyLoadDirective implements OnInit {
  readonly srcAttr = signal<string | undefined>(undefined);
  readonly chkLoaded = signal(false);
  readonly redraw = signal(false);
  readonly display = signal<string | undefined>(undefined);

  readonly imageToLoad = input<string | undefined>(undefined, {
    alias: 'sharedUtilsDirectiveCommonLazyLoad',
  });

  readonly isVideo = input(false);
  readonly willLazyLoad = input(true);
  readonly shouldAutoplay = input(true);

  defaultImage = 'transparent_pixel?$png$';
  loaded = false;
  modernSupport = false;

  private readonly el = inject(ElementRef);
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly environment = inject(Environment);

  constructor() {
    this.srcAttr.set(this.environment.thirdParty.images.scene7proxy.marketing + this.defaultImage);
    this.modernSupport = 'loading' in HTMLImageElement.prototype;

    effect(() => {
      const url = this.imageToLoad();
      if (this.loaded && url) {
        this.srcAttr.set(url);
      }
    });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.modernSupport) {
        this.el.nativeElement.setAttribute('loading', 'lazy');
        this.loadImage();
      } else if (this.canLazyLoad()) {
        this.lazyLoadImage();
      } else {
        this.loadImage();
      }
    }
  }

  private canLazyLoad() {
    return globalThis && 'IntersectionObserver' in globalThis && this.willLazyLoad();
  }

  /* istanbul ignore next */
  private lazyLoadImage() {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0) {
            setTimeout(() => {
              this.loadImage();
              obs.unobserve(this.el.nativeElement);
            }, 250);
          }
        });
      },
      { threshold: [0.1, 0.3, 0.5, 0.7, 0.9] },
    );
    obs.observe(this.el.nativeElement);
  }

  private loadImage() {
    this.srcAttr.set(this.imageToLoad());
    this.chkLoaded.set(true);
    this.loaded = true;

    if (this.isVideo() && this.shouldAutoplay()) {
      this.el.nativeElement.setAttribute('loop', true);
      this.el.nativeElement.setAttribute('autoplay', true);
    }
  }
}
